// Web Badging API helper. Renders the unread notifications count on the installed
// PWA icon. Works in both window and Service Worker (`WorkerNavigator`) contexts and
// on Chromium and installed iOS/iPadOS Home Screen web apps. All calls are
// feature-detected and never throw.

import { BADGE_FLUSH_NUDGE_DELAY_MS } from '../constants/notification-badge';
import { getStoredUnreadCount, setStoredUnreadCount } from './app-badge-store';

type BadgingNavigator = {
  clearAppBadge?: () => Promise<void>;
  setAppBadge?: (count?: number) => Promise<void>;
};

const getBadgingNavigator = (): BadgingNavigator | null => {
  if (typeof navigator === 'undefined') return null;

  const candidate = navigator as unknown as BadgingNavigator;

  return typeof candidate.setAppBadge === 'function' ? candidate : null;
};

export const isBadgingSupported = (): boolean => getBadgingNavigator() !== null;

// Last count persisted from this context. Lets us skip redundant IndexedDB writes
// when the count hasn't changed (e.g. the 60s foreground poll re-applying the same
// value), avoiding a store write on every tick.
let lastPersistedCount: null | number = null;

// Applies a count to the icon badge. A count of 0 clears the badge.
const setBadge = async (count: number): Promise<void> => {
  const badgingNavigator = getBadgingNavigator();

  if (!badgingNavigator) return;

  try {
    if (count > 0) {
      await badgingNavigator.setAppBadge?.(count);
    } else {
      await badgingNavigator.clearAppBadge?.();
    }
  } catch {
    // best-effort — ignore failures
  }
};

// Successful count: persist as the last-known value and apply it (clears at 0).
export const applyUnreadCount = async (count: number): Promise<void> => {
  if (count !== lastPersistedCount) {
    lastPersistedCount = count;
    await setStoredUnreadCount(count);
  }

  await setBadge(count);
};

// Edge/Chromium only flushes the badge overlay to the Windows taskbar on a document
// navigation/reload — live `setAppBadge` calls (window or Service Worker) aren't
// pushed to the OS shell until the next navigation. Toggling the badge off then back
// on nudges Edge to repaint the overlay without a reload. Best-effort and
// feature-detected; a brief flicker is expected. Only call this when the count has
// actually changed (never on the idle re-apply) to avoid flickering on every poll.
export const flushAppBadge = async (count: number): Promise<void> => {
  const badgingNavigator = getBadgingNavigator();

  if (!badgingNavigator) return;

  if (count !== lastPersistedCount) {
    lastPersistedCount = count;
    await setStoredUnreadCount(count);
  }

  try {
    await badgingNavigator.clearAppBadge?.();
  } catch {
    // best-effort — ignore failures
  }

  await new Promise<void>((resolve) => {
    setTimeout(resolve, BADGE_FLUSH_NUDGE_DELAY_MS);
  });

  await setBadge(count);
};

// Failed fetch: re-apply the last-known stored value so the badge survives the
// failure. Never clears the badge; no-op when there is no stored value yet.
export const applyStoredUnreadCountFallback = async (): Promise<void> => {
  const storedCount = await getStoredUnreadCount();

  if (storedCount === null) return;

  await setBadge(storedCount);
};

export type BadgeVisibilityState = {
  isAuthenticated: boolean;
  isEnabled: boolean;
  isErrored: boolean;
  unreadCount: number;
};

// Forces a badge repaint when the installed PWA regains visibility/focus. On
// Edge/Windows the taskbar badge overlay is only committed to the OS shell on a
// navigation; after a minimize -> restore Edge repaints the overlay from its
// last-navigation snapshot, discarding any live in-app increment (the badge appears to
// revert to an older count). A plain re-apply won't repaint it, so we run the
// repaint-forcing `flushAppBadge` regardless of whether the count changed. Returns
// `true` when a live count was flushed (so the caller can also trigger the Service
// Worker repaint path), and `false` when nothing was painted (feature disabled or
// unauthenticated) or only the stored fallback ran (fetch error state).
export const repaintBadgeOnVisibility = async (state: BadgeVisibilityState): Promise<boolean> => {
  const { isAuthenticated, isEnabled, isErrored, unreadCount } = state;

  if (!isAuthenticated || !isEnabled) return false;

  if (isErrored) {
    await applyStoredUnreadCountFallback();

    return false;
  }

  await flushAppBadge(unreadCount);

  return true;
};

// Removes the app icon badge and resets the stored count. Used when the badge
// feature is disabled so a stale value can never be re-applied by the fallback.
export const clearAppBadge = async (): Promise<void> => {
  await setStoredUnreadCount(0);
  lastPersistedCount = 0;
  await setBadge(0);
};
